import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api';
import './DoneFixturesPage.css';

function DoneFixturesPage() {
  const [liveFixtures, setLiveFixtures] = useState([]);
  const [fixtureDetails, setFixtureDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [expandedFixture, setExpandedFixture] = useState(null);
  const [fixtureMarkets, setFixtureMarkets] = useState({});
  
  const navigate = useNavigate();

  useEffect(() => {
    loadDoneFixtures();
  }, []);

  const loadDoneFixtures = async () => {
    try {
      setLoading(true);
      // Same origin + /api prefix as Dashboard (see api/index.js). Avoids loading Done from
      // a hardcoded host while quote/history requests go to the CRA dev server (empty env).
      const { data: payload } = await apiClient.get('/api/done-fixtures', {
        params: { lookback_days: 3 },
      });

      const fixturesData = payload.fixtures || [];
      
      // Extract fixture IDs for the list
      const fixtureIds = fixturesData.map(f => f.fixture_id);
      setLiveFixtures(fixtureIds);
      
      // Build details and markets from the response
      const details = {};
      const markets = {};
      
      for (const fixture of fixturesData) {
        details[fixture.fixture_id] = {
          fixture_id: fixture.fixture_id,
          league: fixture.league,
          team_name: fixture.team_name,
          market_count: fixture.market_count,
          players: fixture.players || [],
          last_update: fixture.last_update,
          start_time: fixture.start_time,
          status: fixture.status,
        };
        markets[fixture.fixture_id] = fixture.markets || [];
      }
      
      setFixtureDetails(details);
      setFixtureMarkets(markets);
      setLastUpdate(new Date());
      setError(null);
    } catch (err) {
      console.error('Failed to load done fixtures:', err);
      setError('Failed to load done fixtures');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (fixtureId) => {
    setExpandedFixture(expandedFixture === fixtureId ? null : fixtureId);
  };

  const goToMarket = (marketKey) => {
    navigate(`/dashboard?market=${encodeURIComponent(marketKey)}`);
  };

  const goToGame = (fixtureId) => {
    navigate(`/game/${encodeURIComponent(fixtureId)}`);
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '—';
    const date = new Date(timestamp * 1000);
    return date.toLocaleString();
  };

  if (loading && liveFixtures.length === 0) {
    return (
      <div className="done-fixtures-page">
        <div className="loading">Loading done fixtures...</div>
      </div>
    );
  }

  return (
    <div className="done-fixtures-page">
      <div className="page-header">
        <h1>Done Fixtures</h1>
        {lastUpdate && (
          <div className="last-update">
            Last updated: {lastUpdate.toLocaleTimeString()}
          </div>
        )}
        <button className="button" onClick={loadDoneFixtures}>
          Refresh
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">{liveFixtures.length}</div>
          <div className="stat-label">Done Fixtures</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">
            {Object.values(fixtureDetails).reduce((sum, d) => sum + d.market_count, 0)}
          </div>
          <div className="stat-label">Total Markets</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">
            {Object.values(fixtureDetails).reduce((sum, d) => sum + d.players.length, 0)}
          </div>
          <div className="stat-label">Unique Players</div>
        </div>
      </div>

      {liveFixtures.length === 0 ? (
        <div className="card">
          <p style={{ textAlign: 'center', color: '#666' }}>
            No done fixtures yet. Fixtures will appear here when games go live or end.
          </p>
        </div>
      ) : (
        <div className="card">
          <h2 className="card-title">Completed Games</h2>
          <p className="card-description">
            These fixtures are no longer receiving updates. Click to view historical data.
          </p>
          
          <table className="data-table">
            <thead>
              <tr>
                <th>Fixture ID</th>
                <th>League</th>
                <th>Team</th>
                <th>Start Time</th>
                <th>Status</th>
                <th>Markets</th>
                <th>Players</th>
                <th>View</th>
              </tr>
            </thead>
            <tbody>
              {liveFixtures.map(fixtureId => {
                const details = fixtureDetails[fixtureId] || {};
                const markets = fixtureMarkets[fixtureId] || [];
                const isExpanded = expandedFixture === fixtureId;
                
                return (
                  <React.Fragment key={fixtureId}>
                    <tr className={isExpanded ? 'expanded-row' : ''}>
                      <td>
                        <code>{fixtureId}</code>
                      </td>
                      <td>{details.league || '—'}</td>
                      <td>{details.team_name || '—'}</td>
                      <td>{formatTimestamp(details.start_time)}</td>
                      <td>
                        {details.status === 'complete' ? (
                          <span className="badge badge-success">Done</span>
                        ) : (
                          <span className="badge">{details.status || '—'}</span>
                        )}
                      </td>
                      <td>{details.market_count || 0}</td>
                      <td>{details.players?.length || 0}</td>
                      <td className="actions-cell">
                        <button
                          className="button button-small button-secondary"
                          onClick={() => goToGame(fixtureId)}
                        >
                          Game
                        </button>
                        <button 
                          className="button button-small"
                          onClick={() => toggleExpand(fixtureId)}
                        >
                          {isExpanded ? 'Hide' : 'View'}
                        </button>
                      </td>
                    </tr>
                    {isExpanded && markets.length > 0 && (
                      <tr className="expanded-content">
                        <td colSpan="8">
                          <div className="markets-list">
                            <h4>Markets for {fixtureId}</h4>
                            <table className="nested-table">
                              <thead>
                                <tr>
                                  <th>Player</th>
                                  <th>Market</th>
                                  <th>Line</th>
                                  <th>P(Fair)</th>
                                  <th>Books</th>
                                  <th>Status</th>
                                  <th></th>
                                </tr>
                              </thead>
                              <tbody>
                                {markets.map(market => (
                                  <tr key={market.market_key}>
                                    <td>{market.player_name || market.player_id}</td>
                                    <td>{market.market_id}</td>
                                    <td>{market.line}</td>
                                    <td>{(market.p_fair * 100).toFixed(1)}%</td>
                                    <td>{market.n_books}</td>
                                    <td>
                                      {market.gate_allowed ? (
                                        <span className="badge badge-success">Gated</span>
                                      ) : (
                                        <span className="badge badge-error">Blocked</span>
                                      )}
                                    </td>
                                    <td>
                                      <button
                                        className="button button-small button-secondary"
                                        onClick={() => goToMarket(market.market_key)}
                                      >
                                        Details
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default DoneFixturesPage;
